/**
 * src/app/ReadOnlyBanner.tsx — WR-04 read-only soft-lock preview banner
 *
 * Sticky banner shown when the user has activated read-only preview from PaywallPage.
 * Communicates the read-only state clearly; provides upgrade and "go to paywall" CTAs.
 *
 * T-16-04: server requireAccess (Phase 15) still returns 402 on writes.
 * The banner is purely informational UX — not an authorization boundary.
 * ACCESS-05: data viewable in read-only mode per design spec.
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useUpgradeModal } from '@/features/billing';

export const ReadOnlyBanner: FC = () => {
  const { t } = useTranslation();
  const { readOnlyPreview, setReadOnlyPreview } = useAuthStore();
  const { handleCheckout } = useUpgradeModal();

  if (!readOnlyPreview) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[60] h-10 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700 px-4 flex items-center justify-between gap-3"
      role="status"
      aria-live="polite"
      aria-label={t('billing.readonly_banner_label', 'Read-only mode')}
    >
      <span className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300 min-w-0 truncate">
        <Lock size={14} className="shrink-0" aria-hidden="true" />
        {t('billing.readonly_banner_text', 'Trial ended — your edits are saved on this device and will reach the cloud once you upgrade.')}
      </span>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => void handleCheckout()}
          className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline underline-offset-2"
        >
          {t('billing.upgrade_cta', 'Upgrade to Pro')}
        </button>
        <span className="text-amber-400 dark:text-amber-600 select-none">·</span>
        <button
          type="button"
          onClick={() => setReadOnlyPreview(false)}
          className="text-sm text-amber-700 dark:text-amber-400 hover:underline underline-offset-2"
        >
          {t('billing.readonly_exit', 'Back to upgrade')}
        </button>
      </div>
    </div>
  );
};
